import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Brain, Send, Sparkles, X, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { api } from '../../lib/apiClient';
import { GUIDE_NO_CREDITS_MESSAGE, isGuideNoCreditsError } from '../../shared/guide';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  sources?: Array<{ slug: string; title: string; route?: string }>;
  pending?: boolean;
}

interface Topic {
  slug: string;
  title: string;
  route?: string;
  tags: string[];
}

const WELCOME: ChatMessage = {
  role: 'assistant',
  content:
    'Oi! Sou o **Guia de Suporte** do Helps. Posso te ajudar a achar telas, botões e caminhos no sistema. É só me perguntar, tipo: *"onde cadastro um contrato?"* ou *"como gero um orçamento?"*',
};

const STORAGE_KEY = 'guide-chat-v2';
const ACTIVITY_KEY = 'guide-chat-v2-activity';
// Janela de inatividade: após esse tempo sem interação, o histórico é descartado.
const IDLE_RESET_MS = 5 * 60 * 1000;
// Histórico enviado à API: 3 turnos batem com MAX_HISTORY_TURNS no backend.
const API_HISTORY_LIMIT = 6;

export function GuideChatFab() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [topics, setTopics] = useState<Topic[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const markActivity = useCallback(() => {
    try {
      sessionStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    } catch {
      // Privacy mode / quota — ignora.
    }
  }, []);

  const resetChat = useCallback(() => {
    setMessages([WELCOME]);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(ACTIVITY_KEY);
    } catch {
      // Ignora.
    }
  }, []);

  // Hidrata estado: se última atividade foi dentro da janela, restaura mensagens.
  useEffect(() => {
    try {
      const lastRaw = sessionStorage.getItem(ACTIVITY_KEY);
      const last = lastRaw ? Number(lastRaw) : 0;
      if (last && Date.now() - last > IDLE_RESET_MS) {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(ACTIVITY_KEY);
        return;
      }
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as ChatMessage[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed);
        }
      }
    } catch {
      // Ignora corrompido.
    }
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch {
      // Quota cheia ou privacy mode — o chat continua em memória.
    }
  }, [messages]);

  useEffect(() => {
    if (!open || topics.length > 0) return;
    api
      .get<Topic[]>('/guide/topics')
      .then(setTopics)
      .catch(() => {
        // Sugestões são opcionais.
      });
  }, [open, topics.length]);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (open) {
      markActivity();
      const t = setTimeout(() => inputRef.current?.focus(), 180);
      return () => clearTimeout(t);
    }
  }, [open, markActivity]);

  // Timer de auto-reset.
  useEffect(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      setMessages([WELCOME]);
      try {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(ACTIVITY_KEY);
      } catch {
        // Ignora.
      }
    }, IDLE_RESET_MS);
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [messages, open]);

  const send = useCallback(
    async (text: string) => {
      const question = text.trim();
      if (!question || loading) return;

      const historyForApi = messages
        .filter((m) => !m.pending)
        .slice(-API_HISTORY_LIMIT)
        .map((m) => ({ role: m.role, content: m.content }));

      setMessages((prev) => [
        ...prev,
        { role: 'user', content: question },
        { role: 'assistant', content: '', pending: true },
      ]);
      setInput('');
      setLoading(true);
      markActivity();

      try {
        const result = await api.post<{
          reply: string;
          sources: Array<{ slug: string; title: string; route?: string }>;
          creditsUsed: number;
          balanceAfter: number;
        }>('/guide/chat', {
          message: question,
          history: historyForApi,
          currentRoute: pathname,
        });
        setMessages((prev) => {
          const next = [...prev];
          const lastIdx = next.length - 1;
          next[lastIdx] = {
            role: 'assistant',
            content: result.reply,
            sources: result.sources,
          };
          return next;
        });
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Não consegui responder agora. Tente de novo em alguns segundos.';
        const content = isGuideNoCreditsError(message)
          ? GUIDE_NO_CREDITS_MESSAGE
          : `Ops, deu erro aqui: ${message}`;
        setMessages((prev) => {
          const next = [...prev];
          const lastIdx = next.length - 1;
          next[lastIdx] = {
            role: 'assistant',
            content,
          };
          return next;
        });
      } finally {
        setLoading(false);
        markActivity();
      }
    },
    [loading, messages, pathname, markActivity],
  );

  return (
    <>
      <button
        type="button"
        aria-label="Abrir guia de suporte"
        onClick={() => setOpen(true)}
        className={cn(
          'guide-fab group fixed right-4 bottom-4 z-[70] flex h-12 w-12 items-center justify-center rounded-full text-white transition-all duration-300',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-dark/50 focus-visible:ring-offset-2',
          open && 'pointer-events-none scale-75 opacity-0',
        )}
      >
        <span className="guide-fab-pulse absolute inset-0 rounded-full" aria-hidden />
        <span className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-brand-dark shadow-[0_6px_18px_-4px_rgba(24,64,89,0.5)] transition-transform group-hover:scale-105">
          <Brain
            className="h-5 w-5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]"
            strokeWidth={1.8}
          />
        </span>
        <span
          className="pointer-events-none absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_0_2px_rgba(24,64,89,1),0_0_6px_rgba(52,211,153,0.6)]"
          aria-hidden
        />
        <span className="sr-only">Guia de suporte</span>
      </button>

      {/*
        Chat flutuante: pop-over não-bloqueante. Sem backdrop, o usuário
        continua interagindo com o sistema por trás.
      */}
      <div
        role="dialog"
        aria-label="Guia de Suporte"
        aria-hidden={!open}
        className={cn(
          'fixed right-4 bottom-4 z-[70] flex w-[360px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white',
          'shadow-[0_18px_50px_-12px_rgba(24,64,89,0.2),0_6px_20px_-8px_rgba(24,64,89,0.1)]',
          'transition-all duration-200 ease-out',
          'h-[min(560px,calc(100vh-5rem))]',
          open
            ? 'translate-y-0 scale-100 opacity-100'
            : 'pointer-events-none translate-y-4 scale-95 opacity-0',
        )}
      >
        <header className="flex items-center justify-between border-b border-gray-100 bg-gradient-to-br from-brand-dark/[0.05] to-brand-dark/[0.02] px-3 py-2.5">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-dark/10 text-brand-dark">
              <Sparkles className="h-3.5 w-3.5" />
            </div>
            <div className="leading-tight">
              <p className="font-display text-[13px] font-medium text-gray-900">Guia de Suporte</p>
              <p className="text-[10px] text-gray-500">Dúvidas de uso do sistema</p>
            </div>
          </div>
          <div className="flex items-center gap-0.5">
            {messages.length > 1 && (
              <button
                type="button"
                onClick={resetChat}
                className="rounded-md px-2 py-1 text-[10px] text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
              >
                Limpar
              </button>
            )}
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Fechar"
              className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </header>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-3 py-3"
          style={{ scrollbarWidth: 'thin' }}
        >
          <div className="flex flex-col gap-2.5">
            {messages.map((m: ChatMessage, i: number) => (
              <React.Fragment key={i}>
                <MessageBubble message={m} />
              </React.Fragment>
            ))}
            {messages.length === 1 && topics.length > 0 && (
              <SuggestedTopics
                topics={topics.slice(0, 5)}
                onPick={(t) => send(`Como usar: ${t.title}`)}
              />
            )}
          </div>
        </div>

        <footer className="border-t border-gray-100 bg-gray-50/40 p-2.5">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
            className="flex items-end gap-1.5"
          >
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send(input);
                }
              }}
              placeholder="Escreva sua dúvida…"
              rows={1}
              disabled={loading}
              className="min-h-8 flex-1 resize-none rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand-dark/30 focus:outline-none focus:ring-2 focus:ring-brand-dark/10 disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              aria-label="Enviar"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-dark text-white transition-opacity hover:bg-brand-dark/90 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5" />
              )}
            </button>
          </form>
        </footer>
      </div>
    </>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';
  return (
    <div className={cn('flex flex-col', isUser ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[90%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed',
          isUser
            ? 'rounded-br-sm bg-brand-dark text-white'
            : 'rounded-bl-sm bg-gray-100 text-gray-900',
        )}
      >
        {message.pending ? (
          <span className="inline-flex items-center gap-1 text-gray-500">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>Consultando o manual…</span>
          </span>
        ) : (
          <InlineMarkdown text={message.content} />
        )}
      </div>
      {message.sources && message.sources.length > 0 && (
        <p className="mt-0.5 text-[9px] text-gray-400">
          Baseado em: {message.sources.map((s) => s.title).join(' · ')}
        </p>
      )}
    </div>
  );
}

function SuggestedTopics({
  topics,
  onPick,
}: {
  topics: Topic[];
  onPick: (t: Topic) => void;
}) {
  return (
    <div className="mt-1 flex flex-wrap gap-1.5">
      {topics.map((t) => (
        <button
          key={t.slug}
          type="button"
          onClick={() => onPick(t)}
          className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[10px] text-gray-500 transition-colors hover:border-brand-dark/30 hover:text-gray-900"
        >
          {t.title.split(' — ')[0]}
        </button>
      ))}
    </div>
  );
}

/**
 * Renderizador leve: negrito (**), itálico (*), listas numeradas e com bullet.
 */
function InlineMarkdown({ text }: { text: string }) {
  const lines = text.split('\n');
  return (
    <div className="flex flex-col gap-1.5">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={i} className="h-1" />;
        const ordered = trimmed.match(/^(\d+)\.\s+(.*)$/);
        if (ordered) {
          return (
            <div key={i} className="flex gap-2">
              <span className="font-medium text-gray-700">{ordered[1]}.</span>
              <span>{renderInline(ordered[2])}</span>
            </div>
          );
        }
        if (trimmed.startsWith('- ')) {
          return (
            <div key={i} className="flex gap-2">
              <span className="text-gray-400">•</span>
              <span>{renderInline(trimmed.slice(2))}</span>
            </div>
          );
        }
        return <p key={i}>{renderInline(trimmed)}</p>;
      })}
    </div>
  );
}

function renderInline(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return (
        <strong key={i} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return (
        <em key={i} className="italic">
          {part.slice(1, -1)}
        </em>
      );
    }
    return <span key={i}>{part}</span>;
  });
}
